import type { Group } from '@microsoft/microsoft-graph-types'
import { t } from 'i18next'
import { atom, PrimitiveAtom } from 'jotai'
import { atomsWithMutation } from 'jotai-tanstack-query'
import cloneDeep from 'lodash/cloneDeep'
import { DateTime } from 'luxon'
import { v4 as uuidv4 } from 'uuid'

import { currentEnv } from '@/env/vars.ts'
import { queryClient } from '@/QueryClient'
import { store } from '@/store'
import { IShortMessageObject } from '@/types/gigiChat/Chat'
import { IConversation, IConversationListItem, IQuestionDisplayData, TQuestionAction } from '@/types/gigiChat/Conversation'
import { IConversationSettings, IDocument, ISnippetSourceDetails, TProcessingStrategy } from '@/types/gigiChat/ConversationSettings'
import { IDebugInternalPrompt } from '@/types/gigiChat/Debug.ts'
import { IPrompt, IPromptApiResponse } from '@/types/gigiChat/Prompts'
import { IFilteredSnippets } from '@/types/gigiChat/Snippets'
import { ITextExample } from '@/types/gigiChat/TextExamples'
import { deepClone } from '@/utils'
import { getAndRemovePropertyFromUrlOnce } from '@/utils/chat.ts'
import { DELETE, GET, POST, SIMPLE_GET } from '@/utils/fetch'
import { filterSnippetSourcesByUserGroups } from '@/utils/systemGroups'
import { changeUrlUuid, isValidUuid } from '@/utils/updateUrl'
import { checkUserMembership } from '@/utils/user'

import { chatVisualsAtom, getChatVisuals } from './Visuals'

export const chatIdAtom: PrimitiveAtom<string> = atom<string>('')
export const partialMessagesAtom: PrimitiveAtom<Array<IShortMessageObject> | Array<IConversation>> = atom<
    Array<IShortMessageObject> | Array<IConversation>
>([])
export const chatSettingsAtom: PrimitiveAtom<IConversationSettings | null> = atom<IConversationSettings | null>(null)
export const isServerRespondingAtom: PrimitiveAtom<boolean> = atom(false)
export const conversationsListItemAtom: PrimitiveAtom<Array<IConversationListItem>> = atom<Array<IConversationListItem>>([])
export const mobileDrawerAtom: PrimitiveAtom<boolean> = atom(false)
export const mobileUploadDrawerAtom: PrimitiveAtom<boolean> = atom(false)
export const userMembershipAtom: PrimitiveAtom<Array<string>> = atom<Array<string>>([])
export const snippetSourcesAtom: PrimitiveAtom<ISnippetSourceDetails> = atom<ISnippetSourceDetails>({
    data: [],
    bundle: [],
    defaultBundleName: '',
})
export const filteredSnippetsAtom: PrimitiveAtom<IFilteredSnippets> = atom<IFilteredSnippets>({
    allowed_snippet_source_enums: [],
    allowed_bundle_configurations: [],
})
export const chatProgressMessageAtom = atom<string | null>('')
export const chatSettingsExpandedAtom: PrimitiveAtom<boolean> = atom(true)
// use this atom just if you want to send a question progamatically
export const newQuestionAtom: PrimitiveAtom<string | null> = atom<string | null>(null)
export const wizzardProcesingStrategyAtom: PrimitiveAtom<TProcessingStrategy | undefined> = atom<TProcessingStrategy | undefined>(undefined)
export const chatExamplesAtom = atom<Array<ITextExample>>([])
export const exampleTypeAtom: PrimitiveAtom<string> = atom<string>('')
export const promptLibraryDrawerAtom: PrimitiveAtom<boolean> = atom(false)

export const cleanData = (shouldClean?: boolean) => {
    store.set(chatIdAtom, '')
    store.set(conversationsListItemAtom, [])
    store.set(partialMessagesAtom, [])

    if (shouldClean) {
        store.set(chatSettingsAtom, null)
    }
}

export const getChatHistory = async (chatId: string) => {
    let cachingData = {}
    const settings = store.get(chatSettingsAtom)

    if (settings?.processingStrategy !== 'Translation') {
        cachingData = {
            gcTime: 24 * 60 * 60 * 1000, // hours * min * sec * miliseconds
            staleTime: 24 * 60 * 60 * 1000, // hours * min * sec * miliseconds
        }
    }

    const res = await queryClient.fetchQuery({
        queryKey: [chatId],
        ...cachingData,
        queryFn: () =>
            GET<{ data: IConversation[] }>(
                `${currentEnv.features.isEnabled('gigiCore') ? currentEnv.coreChatUrl : currentEnv.chatUrl}/chats/${chatId}`,
            ),
    })

    if (res) {
        return res || { data: [] }
    }
    return { data: [] }
}

export const getChatSettings = async (chatId: string) => {
    const res = await GET<IConversationSettings>(
        `${currentEnv.features.isEnabled('gigiCore') ? currentEnv.coreChatUrl : currentEnv.chatUrl}/chats/${chatId}/settings`,
    )
    if (res) {
        return res
    }
}

export const postChatSettings = async (chatId: string, settings: IConversationSettings, updateSettings: boolean = true) => {
    if (updateSettings) {
        updateConversationSettings(chatId, settings)
    }

    return await POST(`${currentEnv.chatUrl}/chats/${chatId}/settings`, settings)
}

const sortConversations = (a: IConversationListItem, b: IConversationListItem) => {
    if (a.lastAskTimestamp && b.lastAskTimestamp) {
        const d1: number = new Date(a.lastAskTimestamp).getTime()
        const d2: number = new Date(b.lastAskTimestamp).getTime()
        if (d1 < d2) {
            return 1
        }
        if (d1 > d2) {
            return -1
        }
        return 0
    }
    return 0
}

export const getConversationsList = async ({ query, isInitial, signal }: { query?: string; isInitial?: boolean; signal?: AbortSignal }) => {
    const res = await GET<{ data: IConversationListItem[] }>(
        `${currentEnv.features.isEnabled('gigiCore') ? currentEnv.coreChatUrl : currentEnv.chatUrl}/chats${query ? '?' + query : ''}`,
        {
            hideError: signal ? true : false,
            signal: signal,
        },
    )
    if (res && res.data && !signal?.aborted) {
        const sortConversationList = res.data.sort(sortConversations)
        const existingChatConversations = store.get(conversationsListItemAtom)
        // in case that the user come back to the chat page we should create a new chat
        // and to add it in the list else we just save the conversation list
        if (isInitial) {
            // NOTE: we should review this logic since we shouldn't have any duplicates on this step but
            // at some point we might have
            const conversations = existingChatConversations.concat(sortConversationList)

            store.set(conversationsListItemAtom, conversations)
        } else if (!isInitial) {
            let newConversationList = sortConversationList
            // in case that the first element is a new chat we should keep it because if we override the
            // conversation list with the list received from the server we will lose the new one
            if (
                existingChatConversations &&
                existingChatConversations.length > 0 &&
                existingChatConversations[0].settings.title === 'New Chat'
            ) {
                newConversationList = [existingChatConversations[0], ...sortConversationList]
            }

            store.set(conversationsListItemAtom, newConversationList)
        }

        return sortConversationList
    } else {
        return []
    }
}

export const updateConversationSettings = (
    chatId: string,
    settings: IConversationSettings,
    translateSettings?: {
        questionAction?: TQuestionAction
        questionDisplayData?: IQuestionDisplayData
    },
) => {
    let conversationsList: IConversationListItem[] = [...store.get(conversationsListItemAtom)]
    const newSettings = deepClone<object>(settings) as IConversationSettings

    //this is temporarry since on node chat api get conversation list is not working
    // NOTE: this should be removed once get conversationlist will return data
    if (conversationsList.length === 0) {
        store.set(chatSettingsAtom, newSettings)
    }

    conversationsList = conversationsList.map((conv) => {
        if (conv.chatId === chatId) {
            //TO DO: please check why we are keeping settings in 2 places? just for title?
            conv.settings = newSettings
            if (translateSettings && translateSettings.questionAction) {
                conv.questionAction = translateSettings?.questionAction
            }
            if (translateSettings && translateSettings.questionDisplayData) {
                conv.questionDisplayData = translateSettings?.questionDisplayData
            }

            store.set(chatSettingsAtom, newSettings)
        }
        return conv
    })

    store.set(conversationsListItemAtom, conversationsList)
}

export const addNewConversation = (chatId: string, settings: IConversationSettings) => {
    let conversationsList = [...store.get(conversationsListItemAtom)]
    const newSettings = deepClone<object>(settings) as IConversationSettings
    const newConversation: IConversationListItem = {
        chatId,
        settings: newSettings,
        lastAskTimestamp: DateTime.now().toString(),
    }

    if (settings.processingStrategy === 'Translation') {
        newConversation.questionAction = 'QTextTranslate'
    }

    if (conversationsList.length === 0) {
        conversationsList = [newConversation]
    } else if (conversationsList.length > 0 && conversationsList[0].settings.title !== 'New Chat') {
        conversationsList = [newConversation, ...conversationsList]
    }

    store.set(conversationsListItemAtom, conversationsList)
}

export const [, deleteConversationAtom] = atomsWithMutation(() => ({
    mutationKey: ['delete'],
    mutationFn: async ({ chatID, processingStrategy }: { chatID: string; processingStrategy?: TProcessingStrategy }) => {
        await DELETE(`${currentEnv.features.isEnabled('gigiCore') ? currentEnv.coreChatUrl : currentEnv.chatUrl}/chats/${chatID}`).then(
            async (response: Response | void) => {
                // snapshot of conversations list before delete
                const existingConversationsList = deepClone(store.get(conversationsListItemAtom))
                queryClient.removeQueries({ queryKey: [chatID] })
                const conversationQuerry = processingStrategy ? `processing_strategy=${processingStrategy}` : undefined
                const conversationsList = await getConversationsList({ query: conversationQuerry })

                if (response && !response.ok) {
                    return
                }

                // if response from server has items, use them
                if (conversationsList?.length) {
                    setConversation(conversationsList[0].chatId, conversationsList[0].settings)
                }
                // if not, then check the snapshot and if there are 2 items, it means that the first one is New chat and it can be used as current conversation
                else if (existingConversationsList.length > 1) {
                    setConversation(existingConversationsList[0].chatId, existingConversationsList[0].settings)
                }
                // if there is only one, create new chat
                else if (existingConversationsList.length === 1) {
                    void createChat(processingStrategy)
                }
            },
        )
    },
}))

export const getChatWelcomeMessage = (settings: IConversationSettings): string => {
    switch (settings?.processingStrategy) {
        case 'QA':
            return t('chat.newChat.welcomeQAMessage')

        case 'Writing':
            return t('chat.newChat.welcomeWritingMessage')

        case 'DocAnalysis':
            return t('chat.newChat.welcomeDocAnalysisMessage')

        case 'Translation':
            return t('chat.newChat.welcomeTranslateMessage')

        default:
            return t('chat.newChat.welcomeQAMessage')
    }
}

const fakeServerMessageObject = (message: string, title?: string | null) => {
    return {
        question: null,
        answer: null,
        userPromptId: null,
        feedback: null,
        fakeServerMessage: {
            title: title || null,
            message,
        },
    } as IShortMessageObject
}

const setPartialMessages = (
    messages: Array<IConversation | IShortMessageObject> = [],
    message?: string,
    settings?: IConversationSettings,
    title?: string | null,
) => {
    if (!message && settings) {
        const settingsVal: IConversationSettings = messages.length ? (messages[0] as IConversation).settings : settings
        const messagesToPush: Array<IShortMessageObject | IConversation> = messages || []

        const welcomeMessage: string = getChatWelcomeMessage(settingsVal)
        const messageObject = fakeServerMessageObject(welcomeMessage, null)

        if (settings.processingStrategy === 'DocAnalysis') {
            const uploadMessage = fakeServerMessageObject(t('chat.newChat.welcomeDocAnalysisUploadMessage'), null)
            messagesToPush.unshift(uploadMessage)
        }

        messagesToPush.unshift(messageObject)

        store.set(partialMessagesAtom, messagesToPush)

        if (settings.processingStrategy === 'Translation') {
            store.set(chatSettingsAtom, settings)
        }
    } else if (message) {
        const partialMessages = [...messages]
        if (partialMessages[partialMessages.length - 1].question === null && partialMessages.length - 1 > 0) {
            partialMessages[partialMessages.length - 1] = fakeServerMessageObject(message, title)
        } else {
            partialMessages.push(fakeServerMessageObject(message, title))
        }
        store.set(partialMessagesAtom, partialMessages)
    }
}

export const setFakeServerPartialMessage = (message: string, title?: string | null, messages?: IShortMessageObject[] | IConversation[]) => {
    const existingMessages = store.get(partialMessagesAtom) || []
    const partialMessages = messages || [...existingMessages]

    setPartialMessages(partialMessages, message, undefined, title)
}

export const createChat = async (processingStrategy?: TProcessingStrategy, newChatId?: string, signal?: AbortSignal) => {
    // prevent creation of new chat if isServerResponding
    if (store.get(isServerRespondingAtom)) return
    let urlStrategy: TProcessingStrategy | null = null
    if (getAndRemovePropertyFromUrlOnce('mode') === 'w') {
        // only handle writing mode
        // Q&A is default so needs no URL param
        // other modes have their own route
        urlStrategy = 'Writing'
    }

    // reset
    store.set(partialMessagesAtom, [])
    store.set(mobileDrawerAtom, false)
    store.set(mobileUploadDrawerAtom, false)
    // new data
    const uuid: string = newChatId || uuidv4()
    store.set(chatIdAtom, uuid)
    const wizzardStrategy = store.get(wizzardProcesingStrategyAtom)
    // current settings
    const settingsAtomData = {
        ...store.get(chatSettingsAtom),
    } as IConversationSettings
    if (settingsAtomData) {
        settingsAtomData.title = 'New Chat'
    }
    // if the current settings is not set then GET default settings
    const tempSettings =
        (settingsAtomData.processingStrategy && settingsAtomData) || ((await getChatSettings(uuid)) as IConversationSettings)

    if (signal?.aborted) {
        return
    }

    // clone solves bug with hitting "new chat" from active prompt library assistant
    // without clone it would show the "exited" UI when returning to this chat.
    const settings = cloneDeep(tempSettings)

    // reset prompt
    if (settings.processingStrategyWriting.mode === 'promptLibrary') {
        // clear updated
        settings.processingStrategyWriting.mode = 'normal'
        // clear new props
        delete settings.processingModeAgent
        delete settings.processingModePromptLibrary
    }

    if (wizzardStrategy) {
        settings.processingStrategy = wizzardStrategy
        updateConversationSettings(uuid, settings)
        store.set(wizzardProcesingStrategyAtom, undefined)
    } else if (urlStrategy) {
        settings.processingStrategy = urlStrategy
        updateConversationSettings(uuid, settings)
    } else if (processingStrategy && (processingStrategy === 'DocAnalysis' || processingStrategy === 'Translation')) {
        settings.processingStrategy = processingStrategy
        updateConversationSettings(uuid, settings)
    } else if (settingsAtomData && settingsAtomData.processingStrategy) {
        updateConversationSettings(uuid, settings)
    }
    // insert welcome message
    setPartialMessages([], undefined, settings)
    // set new settings
    store.set(chatSettingsAtom, settings)
    // create
    void addNewConversation(uuid, settings)

    if (!newChatId && !signal?.aborted) {
        // if we create a new chat by pressing "New Chat" buttons we should replace the url uuid
        changeUrlUuid(uuid)
    }
}

export const setConversation = (
    chatId: string,
    settings?: IConversationSettings,
    processingStrategy?: TProcessingStrategy,
    signal?: AbortSignal,
) => {
    let settingsObj = settings || null
    store.set(chatIdAtom, chatId)
    if (settingsObj) {
        store.set(chatSettingsAtom, settingsObj)
    }
    store.set(partialMessagesAtom, [])
    void getChatHistory(chatId).then(async (value: { data: IConversation[] }) => {
        if (signal?.aborted) {
            return
        }

        const dataToStore = []
        const resData = value.data
        if (resData && Array.isArray(resData) && resData.length) {
            settingsObj = resData[resData.length - 1].settings
            for (let i = 0; i < resData.length; i++) {
                if (i === 0) {
                    dataToStore.push(resData[i])
                } else {
                    if (resData[i - 1].settings.processingStrategy !== resData[i].settings.processingStrategy) {
                        const messageInfo = getHistoryFakeServerMessage(resData[i].settings.processingStrategy)
                        dataToStore.push(fakeServerMessageObject(messageInfo.message, messageInfo.title))
                        dataToStore.push(resData[i])
                    } else {
                        dataToStore.push(resData[i])
                    }
                }
            }

            await getChatVisuals(chatId).then((visuals) => {
                if (signal?.aborted) {
                    return
                }
                store.set(chatVisualsAtom, visuals)
            })
        } else if (resData && Array.isArray(resData) && resData.length === 0 && settingsObj && processingStrategy) {
            const isInvalidUUId = isValidUuid(chatId)
            //we should update the chat id from url if the already existing one is not found but just if the uuit is not a valid one
            const newChatId = isInvalidUUId ? chatId : uuidv4()
            //just if the uuid is invalid chand the uuid with the new one
            !isInvalidUUId && changeUrlUuid(newChatId)
            // if the conversation is not founded we create a new chat
            void createChat(processingStrategy, newChatId, signal)
        }

        if (
            settingsObj &&
            dataToStore.length > 0 &&
            settingsObj.processingStrategy !== 'DocAnalysis' &&
            settingsObj.processingStrategy !== 'Translation'
        ) {
            const settingsMessageInfo = getHistoryFakeServerMessage(settingsObj.processingStrategy)
            dataToStore.push(fakeServerMessageObject(settingsMessageInfo.message, settingsMessageInfo.title))
        }
        if (dataToStore.length) {
            setPartialMessages(dataToStore, undefined, settings)
        } else {
            setPartialMessages([...resData], undefined, settings)
        }
    })
}

const getHistoryFakeServerMessage = (strategy: TProcessingStrategy) => {
    switch (strategy) {
        case 'QA':
            return {
                message: t('chat.settings.messages.qa.message'),
                title: t('chat.settings.messages.qa.title'),
            }

        case 'Writing':
            return {
                message: t('chat.settings.messages.writing.message'),
                title: t('chat.settings.messages.writing.title'),
            }

        default:
            return {
                message: t('chat.settings.messages.qa.message'),
                title: t('chat.settings.messages.qa.title'),
            }
    }
}

export const showSettingsSwitchMessage = (strategy: TProcessingStrategy) => {
    switch (strategy) {
        case 'QA':
            setFakeServerPartialMessage(t('chat.settings.messages.qa.message'), t('chat.settings.messages.qa.title'))
            break
        case 'Writing':
            setFakeServerPartialMessage(t('chat.settings.messages.writing.message'), t('chat.settings.messages.writing.title'))
            break
        default:
            break
    }
}

const delay = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

// Used to prevent scrolling to the last message when feedback is added on the partialMessagesAtom
export const preventMessagesScroll = async () => {
    sessionStorage.setItem('canScroll', '0')
    await delay(1000)
    sessionStorage.setItem('canScroll', '1')
}

export const toggleMobiledrawer = () => {
    store.set(mobileDrawerAtom, !store.get(mobileDrawerAtom))
}

export const toggleUploadMobileDrawer = () => {
    store.set(mobileUploadDrawerAtom, !store.get(mobileUploadDrawerAtom))
}

export const getSnippetSources = async () => {
    const res = await queryClient.fetchQuery({
        queryKey: ['snippetSources'],
        queryFn: () =>
            GET<ISnippetSourceDetails>(
                `${currentEnv.features.isEnabled('gigiCore') ? currentEnv.coreChatUrl : currentEnv.chatUrl}/system/snippet_source_details`,
            ),
    })

    if (res) {
        store.set(snippetSourcesAtom, res)
    }
}

export const getUserMembership = async () => {
    const snippetSources = store.get(snippetSourcesAtom)

    await checkUserMembership(snippetSources).then((item) => {
        let groupArray: string[] = []
        if (item && item.value && item.value.length) {
            groupArray = item.value.map((element: Group) => element.id) as Array<string>
        }
        const filteredSnippets = filterSnippetSourcesByUserGroups(snippetSources, groupArray)
        store.set(filteredSnippetsAtom, filteredSnippets)
        store.set(userMembershipAtom, groupArray)
    })
}

export const checkIfDocumentsAreReady = () => {
    const settings = store.get(chatSettingsAtom)

    return settings?.processingStrategyDocAnalysis.docs.filter((item: IDocument) => !item.ready)
}

export const saveSettings = (settings: IConversationSettings) => {
    store.set(chatSettingsAtom, settings)
}

export const checkHealth = async () => {
    return await SIMPLE_GET(
        currentEnv.features.isEnabled('gigiCore') ? `${currentEnv.healthCheckNodeUrl}` : `${currentEnv.healthCheckUrl}`,
        true,
    )
}

export const updateChatTimestamp = () => {
    const conversations = [...store.get(conversationsListItemAtom)]
    conversations.map((conversation) => {
        if (conversation.chatId === store.get(chatIdAtom)) {
            conversation.lastAskTimestamp = DateTime.now().toString()
        }
    })

    store.set(conversationsListItemAtom, conversations.sort(sortConversations))
}

export const getChatExamples = async () => {
    const res = await GET<Array<ITextExample>>(`${currentEnv.utilityUrl}/examples`)

    if (res) {
        store.set(chatExamplesAtom, res)
    }
}

export const openPromptLibraryDrawer = () => {
    store.set(promptLibraryDrawerAtom, true)
}

export const closePromptLibraryDrawer = () => {
    store.set(promptLibraryDrawerAtom, false)
}

export const getPrompts = async (): Promise<IPrompt[]> => {
    const result: void | { prompts: IPromptApiResponse[] } = await GET<{ prompts: IPromptApiResponse[] }>(`${currentEnv.promptUrl}/prompts`)

    if (result && result.prompts) {
        return result.prompts.map((prompt) => {
            return {
                ...prompt,
                isAssistant: !!(prompt.systemPrompt || prompt.userContext),
            }
        })
    }

    return []
}

export const getChatDebugData = async (userPromptId: number) => {
    return GET<{ data: IDebugInternalPrompt[] }>(`${currentEnv.coreChatUrl}/chats/debug_internal_prompts/${userPromptId}`, {
        hideError: true,
    }).then((res) => {
        if (res) {
            return res.data
        }
        return []
    })
}

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        getChatDebugData: typeof getChatDebugData
    }
}

window.getChatDebugData = getChatDebugData
